// src/rms/pages/AdSales.js
// ============================================================================
// Ad sales console — platform staff, not operators
// ============================================================================
// Mounted outside the operator shell at /ad-sales, because the audience is
// ReserveZoo staff and the data is commercial: budgets, advertisers, pacing.
// An operator must never reach it, and RLS (rms_is_ad_admin) enforces that
// server-side regardless of what the router does.
// ============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import db from "../data";
import { computeAvails } from "../ads/avails";
import { ensureRateCard } from "../ads/rateCard";
import { pacingStatus, ineligibleReason } from "../ads/campaignSource";
import { saveCampaign } from "../ads/campaignService";
import CampaignForm, { blankCampaign, toCampaignDraft } from "../components/CampaignForm";
import { VERTICAL_PRESETS } from "../verticals/presets";
import { PageHeader, Stat, StatusPill, Modal, Field, TextInput, Select, Banner, EmptyState, formatDate } from "../components/ui";
import "../styles/rms.css";
import { getLocale } from "../i18n/locale";

const TABS = [
  { key: "avails", label: "Avails" },
  { key: "advertisers", label: "Advertisers" },
  { key: "orders", label: "Insertion orders" },
  { key: "campaigns", label: "Campaigns" },
  { key: "ratecard", label: "Rate card" },
];

const money = (cents, currency = "USD") => {
  const v = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat(getLocale(), {
      style: "currency", currency, maximumFractionDigits: v % 1 === 0 ? 0 : 2,
    }).format(v);
  } catch {
    return `$${v.toFixed(2)}`;
  }
};

function AdSales() {
  const { tab = "avails" } = useParams();
  const navigate = useNavigate();
  const active = TABS.some((t) => t.key === tab) ? tab : "avails";

  return (
    <div className="rms-root" style={{ minHeight: "100vh" }}>
      <header className="rms-topbar rms-topbar--global">
        <div className="rms-topbar-identity">
          <div className="rms-topbar-business">ReserveZoo — Ad Sales</div>
          <div className="rms-topbar-vertical">Platform console</div>
        </div>
        <div className="rms-topbar-meta">
          <Link className="rms-btn rms-btn--ghost rms-btn--sm" to="/advertise">
            Media kit
          </Link>
          <Link className="rms-btn rms-btn--ghost rms-btn--sm" to="/rms">
            Back to RMS
          </Link>
        </div>
      </header>

      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 24px 64px" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`rms-btn ${active === t.key ? "rms-btn--primary" : "rms-btn--ghost"} rms-btn--sm`}
              onClick={() => navigate(`/ad-sales/${t.key}`)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {active === "avails" ? <AvailsTab /> : null}
        {active === "advertisers" ? <AdvertisersTab /> : null}
        {active === "orders" ? <InsertionOrdersTab /> : null}
        {active === "campaigns" ? <CampaignsTab /> : null}
        {active === "ratecard" ? <RateCardTab /> : null}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------

function AvailsTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    ensureRateCard().then(() => computeAvails().then(setData));
  }, []);

  if (!data) return <p style={{ color: "var(--rms-muted-strong)" }}>Calculating inventory…</p>;

  const totalUnsoldValue = data.rows.reduce((s, r) => s + r.unsoldValueCents, 0);

  return (
    <>
      <PageHeader
        title="Avails"
        subtitle="What we have to sell, by vertical. Sorted by the value of unsold inventory — call the top row first."
      />

      <div className="rms-stat-row">
        <Stat label="Sellable operators" value={data.totalSellableOperators} hint="ad-supported accounts" />
        <Stat
          label="Viewability rate"
          value={`${Math.round(data.viewabilityRate * 100)}%`}
          hint={data.viewabilityIsMeasured ? "measured" : "assumed — not enough data yet"}
        />
        <Stat label="Verticals with inventory" value={data.rows.length} />
        <Stat label="Unsold, at rack" value={money(totalUnsoldValue)} hint="per month" />
      </div>

      {!data.viewabilityIsMeasured ? (
        <Banner tone="warn">
          Viewability is an <strong>assumption</strong> until roughly 100 impressions have been
          recorded. Don't quote these forecasts to an advertiser yet — under-delivering a first IO
          is how a direct-sales relationship ends.
        </Banner>
      ) : null}

      <Banner tone="info">
        <div>
          Forecast = operators × {data.viewsPerOperatorPerDay} screen views/day ×{" "}
          {data.workingDaysPerMonth} working days × {data.slotsPerView} slots, discounted by the
          viewability rate. Deliberately conservative.
        </div>
      </Banner>

      {data.rows.length === 0 ? (
        <EmptyState
          title="No sellable inventory yet"
          message="Inventory appears once operators sign up on the ad-supported plan."
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>Vertical</th>
                <th className="rms-num">Operators</th>
                <th className="rms-num">Viewable / mo</th>
                <th className="rms-num">Committed</th>
                <th className="rms-num">Unsold</th>
                <th className="rms-num">Fill</th>
                <th className="rms-num">Rack CPM</th>
                <th className="rms-num">Unsold value</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.adCategory}>
                  <td>
                    <strong>{r.label}</strong>
                    <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>
                      {r.liveCampaigns} live campaign{r.liveCampaigns === 1 ? "" : "s"}
                    </div>
                  </td>
                  <td className="rms-num">{r.operators}</td>
                  <td className="rms-num">{r.monthlyViewable.toLocaleString(getLocale())}</td>
                  <td className="rms-num">{r.committed.toLocaleString(getLocale())}</td>
                  <td className="rms-num">{r.unsold.toLocaleString(getLocale())}</td>
                  <td className="rms-num">
                    <StatusPill
                      status={r.fillPct >= 80 ? "active" : r.fillPct >= 30 ? "pending" : "cancelled"}
                      label={`${r.fillPct}%`}
                    />
                  </td>
                  <td className="rms-num">{money(r.sidebarCpmCents)}</td>
                  <td className="rms-num">
                    <strong>{money(r.unsoldValueCents)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ----------------------------------------------------------------------------

function AdvertisersTab() {
  const [advertisers, setAdvertisers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    db.list("advertisers", {}, { orderBy: "name" }).then((r) => setAdvertisers(r.data || []));
  }, [rev]);

  const save = async (draft) => {
    const payload = {
      name: draft.name,
      website: draft.website || null,
      ad_categories: draft.ad_categories,
      contact_name: draft.contact_name || null,
      contact_email: draft.contact_email || null,
      billing_email: draft.billing_email || null,
      payment_terms_days: Number(draft.payment_terms_days) || 30,
      status: draft.status,
      notes: draft.notes || null,
    };
    if (draft.id) await db.update("advertisers", draft.id, payload);
    else await db.create("advertisers", payload);
    setEditing(null);
    setRev((n) => n + 1);
  };

  return (
    <>
      <PageHeader
        title="Advertisers"
        subtitle={`${advertisers.length} on file`}
        actions={
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blankAdvertiser())}>
            New advertiser
          </button>
        }
      />

      {advertisers.length === 0 ? (
        <EmptyState
          title="No advertisers yet"
          message="Add the companies you're selling to. Tag each with the verticals they want to reach."
          action={
            <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blankAdvertiser())}>
              New advertiser
            </button>
          }
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>Advertiser</th>
                <th>Verticals</th>
                <th>Contact</th>
                <th>Terms</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {advertisers.map((a) => (
                <tr key={a.id}>
                  <td>
                    <strong>{a.name}</strong>
                    {a.website ? <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>{a.website}</div> : null}
                  </td>
                  <td style={{ fontSize: 12.5 }}>
                    {(a.ad_categories || []).map((c) => c.replace(/_/g, " ")).join(", ") || "—"}
                  </td>
                  <td style={{ fontSize: 12.5 }}>
                    {a.contact_name || "—"}
                    <div style={{ color: "var(--rms-muted)" }}>{a.contact_email}</div>
                  </td>
                  <td style={{ fontSize: 12.5 }}>net {a.payment_terms_days}</td>
                  <td><StatusPill status={a.status === "active" ? "active" : "pending"} label={a.status} /></td>
                  <td className="rms-num">
                    <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setEditing(toAdvertiserDraft(a))}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? <AdvertiserForm draft={editing} onCancel={() => setEditing(null)} onSave={save} /> : null}
    </>
  );
}

function AdvertiserForm({ draft: initial, onCancel, onSave }) {
  const [draft, setDraft] = useState(initial);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const toggleCategory = (cat) =>
    set("ad_categories", draft.ad_categories.includes(cat)
      ? draft.ad_categories.filter((c) => c !== cat)
      : [...draft.ad_categories, cat]);

  return (
    <Modal
      title={draft.id ? `Edit ${draft.name}` : "New advertiser"}
      onClose={onCancel}
      width={680}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => onSave(draft)} disabled={!draft.name.trim()}>
            Save
          </button>
        </>
      }
    >
      <div className="rms-field-grid">
        <Field label="Company" required><TextInput value={draft.name} onChange={(v) => set("name", v)} /></Field>
        <Field label="Website"><TextInput value={draft.website} onChange={(v) => set("website", v)} /></Field>
        <Field label="Contact name"><TextInput value={draft.contact_name} onChange={(v) => set("contact_name", v)} /></Field>
        <Field label="Contact email"><TextInput type="email" value={draft.contact_email} onChange={(v) => set("contact_email", v)} /></Field>
        <Field label="Billing email"><TextInput type="email" value={draft.billing_email} onChange={(v) => set("billing_email", v)} /></Field>
        <Field label="Payment terms (days)">
          <input className="rms-input" type="number" value={draft.payment_terms_days} onChange={(e) => set("payment_terms_days", e.target.value)} />
        </Field>
        <Field label="Status">
          <Select value={draft.status} onChange={(v) => set("status", v)} placeholder="Prospect"
            options={[
              { value: "prospect", label: "Prospect" },
              { value: "active", label: "Active" },
              { value: "paused", label: "Paused" },
              { value: "churned", label: "Churned" },
            ]} />
        </Field>
      </div>

      <div className="rms-field">
        <label className="rms-label">Verticals they want to reach</label>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {VERTICAL_PRESETS.map((p) => (
            <button
              key={p.adCategory}
              type="button"
              className={`rms-btn rms-btn--sm ${draft.ad_categories.includes(p.adCategory) ? "rms-btn--primary" : "rms-btn--ghost"}`}
              onClick={() => toggleCategory(p.adCategory)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------------------

function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    db.list("adCampaigns", {}).then((r) => setCampaigns(r.data || []));
    db.list("adCreatives", {}).then((r) => setCreatives(r.data || []));
    db.list("advertisers", {}, { orderBy: "name" }).then((r) => setAdvertisers(r.data || []));
  }, [rev]);

  const sold = campaigns.filter((c) => c.advertiser_id);

  const save = async (draft) => {
    const result = await saveCampaign(draft, { role: "staff" });
    if (!result.success) return setError(result.error);
    setError(null);
    setEditing(null);
    setRev((n) => n + 1);
  };

  return (
    <>
      <PageHeader
        title="Campaigns"
        subtitle={`${sold.length} sold ${sold.length === 1 ? "flight" : "flights"}`}
        actions={
          <button
            type="button"
            className="rms-btn rms-btn--primary"
            onClick={() => setEditing(blankCampaign())}
            disabled={!advertisers.length}
            title={advertisers.length ? undefined : "Add an advertiser first"}
          >
            New campaign
          </button>
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      <Banner tone="info">
        A campaign only serves when it is <strong>live</strong>, inside its flight dates, under
        budget, on pace, and has at least one <strong>approved</strong> creative at the right IAB
        size. The reason column below says which of those is failing.
      </Banner>

      {sold.length === 0 ? (
        <EmptyState
          title="No sold campaigns yet"
          message="Sold flights appear here. Until then, slots run affiliate and house inventory."
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Flight</th>
                <th className="rms-num">Budget</th>
                <th className="rms-num">Delivered</th>
                <th>Pacing</th>
                <th>Serving</th>
              </tr>
            </thead>
            <tbody>
              {sold.map((c) => {
                const pace = pacingStatus(c);
                const approved = creatives.filter(
                  (cr) => cr.campaign_id === c.id && cr.review_status === "approved" && cr.is_active !== false
                ).length;
                const blocked = ineligibleReason(c) || (approved === 0 ? "no approved creative" : null);
                return (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.name}</strong>
                      <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>
                        {(c.ad_categories || []).join(", ")} · {approved} creative{approved === 1 ? "" : "s"}
                      </div>
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {formatDate(c.starts_at)} → {formatDate(c.ends_at)}
                    </td>
                    <td className="rms-num">
                      {money(c.spent_cents)} / {money(c.budget_cents)}
                    </td>
                    <td className="rms-num">
                      {(c.delivered_impressions || 0).toLocaleString(getLocale())}
                      {c.goal_impressions ? ` / ${c.goal_impressions.toLocaleString(getLocale())}` : ""}
                    </td>
                    <td>
                      {pace ? (
                        <StatusPill
                          status={pace.state === "on_pace" ? "active" : pace.state === "under" ? "overdue" : "pending"}
                          label={pace.state.replace(/_/g, " ")}
                        />
                      ) : "—"}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {blocked ? (
                        <span style={{ color: "var(--rms-danger)" }}>{blocked}</span>
                      ) : (
                        <StatusPill status="active" label="serving" />
                      )}
                      <div style={{ marginTop: 4 }}>
                        <button
                          type="button"
                          className="rms-btn rms-btn--ghost rms-btn--sm"
                          onClick={() =>
                            setEditing(
                              toCampaignDraft(c, creatives.filter((cr) => cr.campaign_id === c.id))
                            )
                          }
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <CampaignForm
          draft={editing}
          mode="staff"
          advertisers={advertisers}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------------------

function InsertionOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [editing, setEditing] = useState(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    db.list("insertionOrders", {}, { orderBy: "starts_on", direction: "desc" }).then((r) => setOrders(r.data || []));
    db.list("advertisers", {}, { orderBy: "name" }).then((r) => setAdvertisers(r.data || []));
    db.list("adCampaigns", {}).then((r) => setCampaigns(r.data || []));
  }, [rev]);

  const advertiserName = (id) => advertisers.find((a) => a.id === id)?.name || "—";

  // What the campaigns funded by this IO have actually delivered, so the
  // committed figure can be checked against the contracted one.
  const deliveredFor = (ioId) =>
    campaigns.filter((c) => c.insertion_order_id === ioId).reduce((s, c) => s + (c.spent_cents || 0), 0);

  const save = async (draft) => {
    const payload = {
      advertiser_id: draft.advertiser_id,
      io_number: draft.io_number,
      name: draft.name,
      po_number: draft.po_number || null,
      total_budget_cents: Math.round(Number(draft.total_budget || 0) * 100),
      currency: "USD",
      starts_on: draft.starts_on,
      ends_on: draft.ends_on,
      status: draft.status,
      signed_at: draft.status === "signed" || draft.status === "live" ? new Date().toISOString() : null,
      notes: draft.notes || null,
    };
    if (draft.id) await db.update("insertionOrders", draft.id, payload);
    else await db.create("insertionOrders", payload);
    setEditing(null);
    setRev((n) => n + 1);
  };

  return (
    <>
      <PageHeader
        title="Insertion orders"
        subtitle="The signed commitment. One IO can fund several campaigns and still reconcile to a single invoice."
        actions={
          <button
            type="button"
            className="rms-btn rms-btn--primary"
            onClick={() => setEditing(blankIO(orders.length))}
            disabled={!advertisers.length}
            title={advertisers.length ? undefined : "Add an advertiser first"}
          >
            New IO
          </button>
        }
      />

      {orders.length === 0 ? (
        <EmptyState
          title="No insertion orders"
          message="Raise one once an advertiser commits to a budget."
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>IO</th>
                <th>Advertiser</th>
                <th>Period</th>
                <th className="rms-num">Contracted</th>
                <th className="rms-num">Delivered</th>
                <th className="rms-num">Invoiced</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((io) => {
                const delivered = deliveredFor(io.id);
                const pct = io.total_budget_cents ? Math.round((delivered / io.total_budget_cents) * 100) : 0;
                return (
                  <tr key={io.id}>
                    <td>
                      <strong>{io.io_number}</strong>
                      <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>{io.name}</div>
                    </td>
                    <td style={{ fontSize: 12.5 }}>{advertiserName(io.advertiser_id)}</td>
                    <td style={{ fontSize: 12.5 }}>
                      {formatDate(io.starts_on)} → {formatDate(io.ends_on)}
                    </td>
                    <td className="rms-num">{money(io.total_budget_cents)}</td>
                    <td className="rms-num">
                      {money(delivered)}
                      <div style={{ fontSize: 11, color: "var(--rms-muted)" }}>{pct}%</div>
                    </td>
                    <td className="rms-num">{money(io.invoiced_cents)}</td>
                    <td>
                      <StatusPill
                        status={io.status === "live" || io.status === "signed" ? "active" : io.status === "cancelled" ? "cancelled" : "pending"}
                        label={io.status}
                      />
                    </td>
                    <td className="rms-num">
                      <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setEditing(toIODraft(io))}>
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <Modal
          title={editing.id ? `Edit ${editing.io_number}` : "New insertion order"}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button
                type="button"
                className="rms-btn rms-btn--primary"
                onClick={() => save(editing)}
                disabled={!editing.advertiser_id || !editing.name.trim()}
              >
                Save
              </button>
            </>
          }
        >
          <div className="rms-field-grid">
            <Field label="IO number" required>
              <TextInput value={editing.io_number} onChange={(v) => setEditing({ ...editing, io_number: v })} />
            </Field>
            <Field label="Advertiser" required>
              <Select
                value={editing.advertiser_id}
                onChange={(v) => setEditing({ ...editing, advertiser_id: v })}
                placeholder="Choose…"
                options={advertisers.map((a) => ({ value: a.id, label: a.name }))}
              />
            </Field>
            <Field label="Name" required>
              <TextInput value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} placeholder="Q3 2026 — marine" />
            </Field>
            <Field label="Client PO">
              <TextInput value={editing.po_number} onChange={(v) => setEditing({ ...editing, po_number: v })} />
            </Field>
            <Field label="Total budget" required>
              <input className="rms-input" type="number" min="0" step="0.01" value={editing.total_budget} onChange={(e) => setEditing({ ...editing, total_budget: e.target.value })} />
            </Field>
            <Field label="Status">
              <Select
                value={editing.status}
                onChange={(v) => setEditing({ ...editing, status: v })}
                placeholder="Draft"
                options={[
                  { value: "draft", label: "Draft" },
                  { value: "sent", label: "Sent to client" },
                  { value: "signed", label: "Signed" },
                  { value: "live", label: "Live" },
                  { value: "completed", label: "Completed" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
              />
            </Field>
            <Field label="Starts" required>
              <input className="rms-input" type="date" value={editing.starts_on} onChange={(e) => setEditing({ ...editing, starts_on: e.target.value })} />
            </Field>
            <Field label="Ends" required>
              <input className="rms-input" type="date" value={editing.ends_on} onChange={(e) => setEditing({ ...editing, ends_on: e.target.value })} />
            </Field>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function blankIO(count) {
  const today = new Date();
  const in90 = new Date(today.getTime() + 90 * 864e5);
  const iso = (d) => d.toISOString().slice(0, 10);
  return {
    id: null,
    io_number: `IO-${String(count + 1).padStart(4, "0")}`,
    advertiser_id: "",
    name: "",
    po_number: "",
    total_budget: "",
    status: "draft",
    starts_on: iso(today),
    ends_on: iso(in90),
    notes: "",
  };
}

function toIODraft(io) {
  return {
    id: io.id,
    io_number: io.io_number,
    advertiser_id: io.advertiser_id,
    name: io.name,
    po_number: io.po_number || "",
    total_budget: ((io.total_budget_cents || 0) / 100).toString(),
    status: io.status,
    starts_on: (io.starts_on || "").slice(0, 10),
    ends_on: (io.ends_on || "").slice(0, 10),
    notes: io.notes || "",
  };
}

// ----------------------------------------------------------------------------

function RateCardTab() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    ensureRateCard().then(() =>
      db.list("adRateCard", {}, { orderBy: "ad_category" }).then((r) => setRows(r.data || []))
    );
  }, []);

  const byCategory = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!map[r.ad_category]) map[r.ad_category] = {};
      map[r.ad_category][r.placement] = r;
    });
    return map;
  }, [rows]);

  const labelOf = VERTICAL_PRESETS.reduce((acc, p) => ({ ...acc, [p.adCategory]: p.label }), {});

  return (
    <>
      <PageHeader
        title="Rate card"
        subtitle="Rack rates by vertical. Premium verticals carry high customer lifetime value, so a buyer can pay more to reach a small, exactly-qualified list."
      />
      <div className="rms-table-wrap">
        <table className="rms-table">
          <thead>
            <tr>
              <th>Vertical</th>
              <th className="rms-num">Rail CPM</th>
              <th className="rms-num">Header CPM</th>
              <th className="rms-num">CPC</th>
              <th className="rms-num">Floor</th>
              <th className="rms-num">Min spend</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(byCategory).map(([cat, p]) => (
              <tr key={cat}>
                <td><strong>{labelOf[cat] || cat.replace(/_/g, " ")}</strong></td>
                <td className="rms-num">{money(p.sidebar?.rack_cpm_cents)}</td>
                <td className="rms-num">{money(p.banner_top?.rack_cpm_cents)}</td>
                <td className="rms-num">{money(p.sidebar?.rack_cpc_cents)}</td>
                <td className="rms-num" style={{ color: "var(--rms-muted)" }}>{money(p.sidebar?.floor_cpm_cents)}</td>
                <td className="rms-num">{money(p.sidebar?.min_spend_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function blankAdvertiser() {
  return {
    id: null, name: "", website: "", ad_categories: [], contact_name: "", contact_email: "",
    billing_email: "", payment_terms_days: 30, status: "prospect", notes: "",
  };
}

function toAdvertiserDraft(a) {
  return {
    id: a.id, name: a.name, website: a.website || "", ad_categories: a.ad_categories || [],
    contact_name: a.contact_name || "", contact_email: a.contact_email || "",
    billing_email: a.billing_email || "", payment_terms_days: a.payment_terms_days ?? 30,
    status: a.status, notes: a.notes || "",
  };
}

export default AdSales;
